// SPDX-License-Identifier: AGPL-3.0-or-later

import {
	type RemoteAudioTrack,
	type RemoteParticipant,
	type RemoteTrack,
	type RemoteTrackPublication,
	type Room,
	RoomEvent,
	Track,
} from 'livekit-client';

/** Owns only the AI track in the personal companion room. */
export class CompanionVoiceOutput {
	private readonly elements = new Map<RemoteAudioTrack, HTMLAudioElement>();
	private paused = false;
	private volume = 1;
	private sinkId = 'default';
	private unbind: (() => void) | null = null;

	constructor(private readonly blocked: (value: boolean) => void) {}

	bind(room: Room, participantIdentity: string): void {
		this.disconnect();
		const subscribed = (track: RemoteTrack, _publication: RemoteTrackPublication, participant: RemoteParticipant) => {
			if (track.kind !== Track.Kind.Audio || participant.identity !== participantIdentity) return;
			const audioTrack = track as RemoteAudioTrack;
			const element = audioTrack.attach();
			element.hidden = true;
			element.muted = this.paused;
			element.volume = this.volume;
			this.elements.set(audioTrack, element);
			document.body.append(element);
			this.applySink(element);
			void element
				.play()
				.then(() => {
					if (this.elements.get(audioTrack) === element) this.blocked(false);
				})
				.catch(() => {
					if (this.elements.get(audioTrack) === element) this.blocked(true);
				});
		};
		const unsubscribed = (track: RemoteTrack) => {
			const element = this.elements.get(track as RemoteAudioTrack);
			if (element) {
				(track as RemoteAudioTrack).detach(element);
				element.remove();
				this.elements.delete(track as RemoteAudioTrack);
			}
		};
		room.on(RoomEvent.TrackSubscribed, subscribed);
		room.on(RoomEvent.TrackUnsubscribed, unsubscribed);
		this.unbind = () => {
			room.off(RoomEvent.TrackSubscribed, subscribed);
			room.off(RoomEvent.TrackUnsubscribed, unsubscribed);
		};
	}

	setPaused(paused: boolean): void {
		this.paused = paused;
		for (const element of this.elements.values()) element.muted = paused;
	}

	configure(volume: number, sinkId: string): void {
		this.volume = Math.max(0, Math.min(1, volume));
		this.sinkId = sinkId;
		for (const element of this.elements.values()) {
			element.volume = this.volume;
			this.applySink(element);
		}
	}

	async resume(): Promise<void> {
		await Promise.all([...this.elements.values()].map((element) => element.play()));
		this.blocked(false);
	}

	disconnect(): void {
		this.unbind?.();
		this.unbind = null;
		this.paused = false;
		for (const [track, element] of this.elements) {
			element.pause();
			track.detach(element);
			element.remove();
		}
		this.elements.clear();
		this.blocked(false);
	}

	private applySink(element: HTMLAudioElement): void {
		if ('setSinkId' in element)
			void element.setSinkId(this.sinkId).catch(() => {
				if ([...this.elements.values()].includes(element)) this.blocked(true);
			});
	}
}
